import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, ShoppingCart, Star, Tag, Target } from 'lucide-react';
import { productService } from '../services/api';
import toast from 'react-hot-toast';
import BannerCarousel from './BannerCarousel';

const ProductGrid = ({ onProductSelect }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    setCurrentPage(1);
    fetchProducts(1);
  }, [filter]);

  const fetchProducts = async (page = 1) => {
    try {
      if (page === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      
      const response = await productService.getAll(page, 8);
      const data = response.data;
      
      if (page === 1) {
        setProducts(data.products);
      } else {
        setProducts(prev => [...prev, ...data.products]);
      }
      
      setPagination(data.pagination);
      setCurrentPage(page);
    } catch (error) {
      toast.error('Error al cargar productos');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreProducts = () => {
    if (pagination && pagination.has_next) {
      fetchProducts(currentPage + 1);
    }
  };

  const categories = [
    { id: 'all', name: 'Todos', icon: '⚽' },
    { id: 'camisetas', name: 'Camisetas', icon: '👕' },
    { id: 'shorts', name: 'Shorts', icon: '🩳' },
    { id: 'buzos', name: 'Buzos', icon: '🧥' },
    { id: 'medias', name: 'Medias', icon: '🧦' },
    { id: 'gorras', name: 'Gorras', icon: '🧢' },
    { id: 'accesorios', name: 'Accesorios', icon: '🎽' },
    { id: 'varios', name: 'Varios', icon: '🏆' },
  ];

  const filteredProducts = filter === 'all' 
    ? products 
    : products.filter(product => product.category.toLowerCase() === filter.toLowerCase());

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg shadow-md overflow-hidden animate-pulse">
              <div className="bg-gray-300 h-48"></div>
              <div className="p-4">
                <div className="h-4 bg-gray-300 rounded mb-2"></div>
                <div className="h-4 bg-gray-300 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gray-300 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Banner Carousel - Estilo OnCity */}
      <BannerCarousel />

      {/* Categories Section - Circular Grid Style OnCity */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mb-12"
      >
        <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">Categorías</h2>
        <div className="grid grid-cols-4 md:grid-cols-8 gap-4 md:gap-6">
          {categories.map((category) => (
            <motion.button
              key={category.id}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setFilter(category.id)}
              className={`flex flex-col items-center justify-center p-4 rounded-full transition-all ${
                filter === category.id
                  ? 'bg-gradient-to-br from-red-600 to-red-800 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-red-50 border border-gray-200 shadow-md'
              }`}
            >
              <span className="text-3xl mb-2">{category.icon}</span>
              <span className="text-xs font-medium text-center">{category.name}</span>
            </motion.button>
          ))}
        </div>
      </motion.div>

      {/* Products Grid - Estilo OnCity */}
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4"
      >
        {filteredProducts.map((product) => (
          <motion.div
            key={product.id}
            variants={item}
            whileHover={{ y: -5 }}
            className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-xl transition-all duration-300 border border-gray-200"
            data-testid={`product-card-${product.id}`}
          >
            <Link to={`/product/${product.id}`}>
              <div className="relative">
                <img
                  src={product.images?.[0] || 'https://placehold.co/300x300/cccccc/ffffff?text=Sin+Imagen'}
                  alt={product.name}
                  className="w-full h-48 object-cover"
                  loading="lazy"
                />
                
                {product.discount_percentage > 0 && (
                  <div className="absolute top-2 right-2 bg-red-600 text-white px-3 py-1 rounded text-xs font-bold">
                    {product.discount_percentage}% OFF
                  </div>
                )}
              </div>
            </Link>

            <div className="p-3">
              <Link to={`/product/${product.id}`}>
                <h3 className="font-medium text-sm mb-2 text-gray-800 line-clamp-2 hover:text-red-600 transition-colors">
                  {product.name}
                </h3>
              </Link>
              
              <div className="flex flex-col space-y-1 mb-3">
                {product.discount_percentage > 0 && (
                  <span className="text-xs text-gray-400 line-through">
                    ${product.price}
                  </span>
                )}
                <span className="text-lg font-bold text-gray-900">
                  ${product.discount_percentage > 0 
                    ? (product.price * (1 - product.discount_percentage / 100)).toFixed(2)
                    : product.price}
                </span>
              </div>
              
              <Link to={`/product/${product.id}`}>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full bg-red-600 text-white py-2 text-sm rounded font-medium hover:bg-red-700 transition-colors"
                  data-testid={`add-to-cart-btn-${product.id}`}
                >
                  VER PRODUCTO
                </motion.button>
              </Link>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Load More Button */}
      {pagination && pagination.has_next && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex justify-center mt-12"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={loadMoreProducts}
            disabled={loadingMore}
            className="bg-gradient-to-r from-red-600 to-red-800 text-white px-8 py-3 rounded-full font-semibold hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loadingMore ? (
              <div className="flex items-center space-x-2">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Cargando...</span>
              </div>
            ) : (
              'Cargar más productos'
            )}
          </motion.button>
        </motion.div>
      )}

      {/* Pagination Info */}
      {pagination && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center mt-8"
        >
          <p className="text-gray-600">
            Mostrando {products.length} de {pagination.total_products} productos
          </p>
        </motion.div>
      )}

      {filteredProducts.length === 0 && !loading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <div className="text-6xl mb-4">⚽</div>
          <h3 className="text-2xl font-semibold text-gray-700 mb-2">
            No se encontraron productos
          </h3>
          <p className="text-gray-500">
            Prueba con una categoría diferente o vuelve pronto para ver nuevos productos del club
          </p>
        </motion.div>
      )}
    </div>
  );
};

export default ProductGrid;
